import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { MERCHANT_ROUTES } from '@dukene/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchPaymentLinks, updatePaymentLinkStatus, PaymentLink } from '../../api/paymentLinksApi';
import {
  Link as PhosphorLink,
  Copy,
  Plus,
  Check,
  QrCode,
  Money,
  CursorClick,
  MagnifyingGlass,
  ArrowSquareOut,
  X,
  Spinner,
} from '@phosphor-icons/react';

export default function PaymentLinksList() {
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [qrModalLink, setQrModalLink] = useState<PaymentLink | null>(null);

  const payDomain = import.meta.env.VITE_PAY_DOMAIN || 'https://pay.dukene.co.ke';

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['paymentLinks'],
    queryFn: fetchPaymentLinks,
  });

  const { mutate: changeStatus } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED' }) =>
      updatePaymentLinkStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentLinks'] });
    },
  });

  const handleCopy = (referenceCode: string) => {
    const url = `${payDomain}/${referenceCode}`;
    navigator.clipboard.writeText(url);
    setCopiedId(referenceCode);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filtered = links.filter((item) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      item.title?.toLowerCase().includes(q) || item.referenceCode.toLowerCase().includes(q)
    );
  });

  const totalClicks = links.reduce((acc, l) => acc + (l.clickCount || 0), 0);
  const totalPayments = links.reduce((acc, l) => acc + (l.paymentCount || 0), 0);
  const totalRevenue = links.reduce((acc, l) => acc + Number(l.paidAmount || 0), 0);

  return (
    <div className="space-y-5">
      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E3F1DF] dark:bg-[#006838]/20 flex items-center justify-center text-[#006838] shrink-0">
            <PhosphorLink weight="fill" className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-bold text-[#202223] dark:text-white">
              Instant Payment & Checkout Links
            </h1>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Create shareable direct payment URLs for WhatsApp chats, Instagram DMs, and invoices
            </p>
          </div>
        </div>

        <RouterLink
          to={`${MERCHANT_ROUTES.CHECKOUT_LINKS}/new`}
          className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors shrink-0"
        >
          <Plus weight="bold" className="w-3.5 h-3.5" />
          Create Payment Link
        </RouterLink>
      </div>

      {/* KPI Tiles Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Active Links
          </span>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-0.5">
            {links.filter((l) => l.status === 'ACTIVE').length}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Total Clicks / Visits
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <CursorClick weight="fill" className="w-4 h-4 text-[#006FBB]" />
            <span className="text-xl font-bold text-[#202223] dark:text-white font-mono">
              {totalClicks}
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Completed Payments
          </span>
          <p className="text-xl font-bold text-[#006838] font-mono mt-0.5">
            {totalPayments}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
            Collected Volume
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            <Money weight="fill" className="w-4 h-4 text-[#006838]" />
            <span className="text-xl font-bold text-[#006838] font-mono">
              KES {totalRevenue.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3 shadow-sm flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
          />
          <input
            type="text"
            placeholder="Search payment links by title or reference..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
          />
        </div>

        <span className="text-xs text-[#5C5F62]">
          Showing <b>{filtered.length}</b> payment links
        </span>
      </div>

      {/* Directory Table */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-14 text-center flex flex-col items-center justify-center">
             <Spinner className="w-6 h-6 animate-spin text-[#006838] mb-2" />
             <p className="text-xs text-[#5C5F62]">Loading payment links...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <PhosphorLink weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-2" />
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">
              No Payment Links Found
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">
              Create your first checkout link to start collecting money without a store website.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62]">
                  <th className="py-2.5 px-4">Link Purpose / Title</th>
                  <th className="py-2.5 px-4">Direct URL</th>
                  <th className="py-2.5 px-4">Amount</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4">Conversion Analytics</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {filtered.map((link) => {
                  const isCopied = copiedId === link.referenceCode;
                  const url = `${payDomain}/${link.referenceCode}`;
                  const isActive = link.status === 'ACTIVE';

                  return (
                    <tr
                      key={link.id}
                      className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-4 font-semibold text-[#202223] dark:text-white">
                        {link.title || 'Product Payment'}
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-[#006FBB] truncate max-w-56">
                            {url}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopy(link.referenceCode)}
                            className="h-6 px-2 rounded bg-[#F6F6F7] dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 hover:border-[#8C9196] inline-flex items-center gap-1 text-[11px] font-semibold text-[#202223] dark:text-white transition-colors"
                          >
                            {isCopied ? (
                              <>
                                <Check weight="bold" className="w-3 h-3 text-[#006838]" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy weight="bold" className="w-3 h-3 text-[#5C5F62]" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </td>

                      <td className="py-3 px-4 font-mono font-bold text-[#202223] dark:text-white">
                        KES {Number(link.amount).toLocaleString()}
                      </td>

                      <td className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => changeStatus({ id: link.id, status: isActive ? 'PAUSED' : 'ACTIVE' })}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold cursor-pointer transition-colors ${
                            isActive
                              ? 'bg-[#E3F1DF] text-[#006838] hover:bg-[#cbe8c4]'
                              : 'bg-[#F6F6F7] text-[#5C5F62] hover:bg-[#e6e8eb]'
                          }`}
                          title={isActive ? "Click to Pause" : "Click to Activate"}
                        >
                          {link.status}
                        </button>
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <span className="text-[#202223] dark:text-white font-semibold">
                            {link.paymentCount || 0} paid
                          </span>
                          <span className="text-[#5C5F62]">•</span>
                          <span className="text-[#5C5F62]">
                            {link.clickCount || 0} clicks
                          </span>
                        </div>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setQrModalLink(link)}
                            className="h-7 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] font-semibold inline-flex items-center gap-1 transition-colors"
                          >
                            <QrCode weight="bold" className="w-3.5 h-3.5" />
                            QR
                          </button>

                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="h-7 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#202223] dark:text-white font-semibold inline-flex items-center gap-1 transition-colors"
                          >
                            Open
                            <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {qrModalLink && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl max-w-sm w-full p-5 shadow-xl text-center space-y-4">
            <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
              <span className="font-bold text-sm text-[#202223] dark:text-white">
                Shareable QR Code
              </span>
              <button
                type="button"
                onClick={() => setQrModalLink(null)}
                className="p-1 rounded-lg text-[#5C5F62] hover:bg-[#F6F6F7]"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-white p-4 rounded-xl border border-[#D2D5D9] inline-block mx-auto shadow-xs">
              <QrCode weight="fill" className="w-40 h-40 text-[#202223] mx-auto" />
            </div>

            <div>
              <p className="font-bold text-sm text-[#202223] dark:text-white">
                {qrModalLink.title}
              </p>
              <p className="text-xs font-mono font-bold text-[#006838] mt-0.5">
                KES {Number(qrModalLink.amount).toLocaleString()}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setQrModalLink(null)}
              className="w-full h-8 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
